import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/AppIcon";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import {
  getBusinessCredits,
  purchaseBusinessCredits,
  listBusinessCreditPurchases,
} from "../api/client";

// Buy Credits — tops up the organization WALLET (purchased, available credits).
// The purchase is instant/simulated (no real payment yet). Each candidate invite
// later reserves one credit directly from this wallet. PRICE_PER_CREDIT is an
// indicative estimate for the UI only — nothing is charged; the backend records
// credits, not money.
const PRICE_PER_CREDIT = 49; // ₹ per credit (indicative)

const fmt = (n) => Number(n ?? 0).toLocaleString("en-IN");

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({ icon, label, value, hint, accent }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span
          className={
            "flex h-8 w-8 items-center justify-center rounded-lg " +
            (accent || "bg-primary/10 text-primary")
          }
        >
          <Icon name={icon} size={16} />
        </span>
      </div>
      <div className="mt-3 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export default function BuyCredits() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [amount, setAmount] = useState("");
  const [buying, setBuying] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [totals, history] = await Promise.all([
        getBusinessCredits(),
        listBusinessCreditPurchases({ limit: 50 }),
      ]);
      setWallet(totals);
      setPurchases(history.purchases || []);
    } catch (err) {
      setLoadError(err?.error || "Could not load your wallet.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const parsed = parseInt(amount, 10);
  const validAmount = !Number.isNaN(parsed) && parsed > 0;
  const estimate = validAmount ? parsed * PRICE_PER_CREDIT : 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    setFormError("");
    setSuccess("");
    if (!validAmount) {
      setFormError("Enter a whole number of credits greater than zero.");
      return;
    }
    setBuying(true);
    try {
      const res = await purchaseBusinessCredits({ amount: parsed });
      setSuccess(
        `Added ${fmt(parsed)} credit${parsed === 1 ? "" : "s"}. Wallet balance is now ${fmt(
          res?.walletBalance
        )}.`
      );
      setAmount("");
      await load();
    } catch (err) {
      setFormError(err?.error || "Could not complete the purchase.");
    } finally {
      setBuying(false);
    }
  };

  const walletBalance = wallet?.walletBalance ?? 0;
  const inUse = wallet?.activeReservations ?? 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Buy credits</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Top up your wallet. One credit covers one candidate interview.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => navigate("/business/credits")}
        >
          View activity
        </Button>
      </div>

      {/* Wallet + context stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          icon="Wallet"
          label="Wallet balance"
          value={loading ? "…" : fmt(walletBalance)}
          hint="Available to invite candidates"
          accent="bg-success/15 text-success"
        />
        <StatCard
          icon="Clock"
          label="In use"
          value={loading ? "…" : fmt(inUse)}
          hint="Reserved by open invites"
        />
      </div>

      {loadError && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          <span>{loadError}</span>
          <button onClick={load} className="ml-auto font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* Buy panel */}
      <div className="rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icon name="CreditCard" size={18} />
          </span>
          <div>
            <h2 className="text-base font-semibold text-foreground">Add credits to your wallet</h2>
            <p className="text-xs text-muted-foreground">
              Enter how many credits you want to buy.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="sm:max-w-xs sm:flex-1">
              <Input
                label="Credits to buy"
                type="number"
                step="1"
                min="1"
                placeholder="e.g. 500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
            </div>
            <div className="sm:pb-1">
              <div className="text-xs text-muted-foreground">Estimated total</div>
              <div className="text-xl font-semibold tabular-nums text-foreground">
                ₹{fmt(estimate)}
              </div>
              <div className="text-[11px] text-muted-foreground">
                at ₹{PRICE_PER_CREDIT}/credit
              </div>
            </div>
          </div>

          {formError && (
            <p className="flex items-center gap-1.5 text-sm text-destructive">
              <Icon name="AlertCircle" size={14} /> {formError}
            </p>
          )}
          {success && (
            <p className="flex items-center gap-1.5 text-sm text-success">
              <Icon name="CheckCircle2" size={14} /> {success}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              iconName="Plus"
              loading={buying}
              disabled={buying || !validAmount}
            >
              Buy {validAmount ? fmt(parsed) : ""} credits
            </Button>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              <Icon name="Info" size={12} />
              Simulated — no payment is collected yet. Credits are added instantly.
            </span>
          </div>
        </form>
      </div>

      {/* Purchase history */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold text-foreground">Purchase history</h2>
          <Icon name="History" size={16} className="text-muted-foreground" />
        </div>
        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-12 animate-pulse rounded-lg bg-muted/50" />
            ))}
          </div>
        ) : purchases.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground">
            No purchases yet. Your wallet top-ups will appear here.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {purchases.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 px-5 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    +{fmt(p.credits)} credits
                    {p.status && p.status !== "completed" ? (
                      <span className="ml-2 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-medium capitalize text-warning">
                        {p.status}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatDateTime(p.createdAt)}
                    {p.method ? <span className="capitalize"> · {p.method}</span> : null}
                  </div>
                </div>
                {p.balanceAfter != null && (
                  <span className="flex-shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                    bal {fmt(p.balanceAfter)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
